/**
 * El Embedded Signup de Meta, en puro (D29): qué significa cada evento que el popup manda por
 * `postMessage`, y cuándo hay lo necesario para conectar el canal.
 *
 * Meta manda dos cosas por dos caminos, y en cualquier orden: el `code` por el callback de `FB.login`, y
 * la cuenta y el número por `postMessage`. `useEmbeddedSignup` pone el SDK, el listener y los timers; acá
 * no hay nada del navegador, para poder probar cada orden en que llegan.
 */

/** El `type` de los mensajes del Embedded Signup. Cualquier otro `postMessage` no es de este flujo. */
export const TIPO_DE_MENSAJE = "WA_EMBEDDED_SIGNUP";

/** Si en 10 minutos no llegó el fin del flujo, se da por cancelado (D29). */
export const TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Cuánto se espera al otro camino después de que llegó uno: los ids si llegó el `code`, o el motivo si el
 * login volvió sin `code`. El `code` vive 30 s y el backend lo canjea primero, así que unos segundos no
 * lo ponen en riesgo; y si los ids no llegan, el backend resuelve la cuenta y el número solo.
 */
export const ESPERA_MS = 3000;

/** El cuerpo de `POST /channels/:id/cloud/embedded-signup`. */
export interface ConexionPedida {
  code: string;
  wabaId?: string;
  phoneNumberId?: string;
  isCoexistence?: boolean;
}

export type Resultado =
  | { kind: "no_soportado"; mensaje: string }
  | { kind: "error"; mensaje: string }
  | { kind: "cancelado"; mensaje?: string };

export type EventoDeMeta =
  | { kind: "fin"; coexistence: boolean; wabaId?: string; phoneNumberId?: string }
  | Resultado
  /** Un paso intermedio, o un evento que no se conoce: no cierra nada. */
  | { kind: "paso" }
  /** Un `postMessage` que no es del Embedded Signup. */
  | { kind: "ajeno" };

/** Los fines que no conectan un número de la app, con lo que se le dice al admin. */
const FINES_NO_SOPORTADOS: Record<string, string> = {
  FINISH_ONLY_WABA:
    "Se creó la cuenta de WhatsApp Business pero sin conectar un número. Volvé a intentar y elegí el " +
    "número que usás en la app WhatsApp Business.",
  FINISH_OBO_MIGRATION:
    "El flujo migró una cuenta que administraba otro proveedor, y ese caso no está soportado.",
  FINISH_GRANT_ONLY_API_ACCESS:
    "El flujo sólo dio acceso a la cuenta, sin conectar el número de la app. Volvé a intentar con el " +
    "número de WhatsApp Business.",
};

/** Los errores de onboarding que Meta documenta, dichos para el admin del Store (D29). */
export const MOTIVOS_DE_META: Record<string, string> = {
  "3441045":
    "Ese número no tiene actividad suficiente en la app WhatsApp Business: tiene que venir usándose en la " +
    "app antes de conectarlo.",
  "2655093":
    "La cuenta de WhatsApp Business de ese número ya está compartida con otro proveedor. Hay que quitarle " +
    "ese acceso antes de conectarla acá.",
  "3441049":
    "El número está conectado a otra plataforma. Desconectalo desde la app WhatsApp Business, esperá 15 " +
    "minutos y volvé a intentar.",
  "2655094":
    "El número está conectado a otra plataforma. Desconectalo desde la app WhatsApp Business, esperá 15 " +
    "minutos y volvé a intentar.",
  "2655082": "El número está vinculado a una Página de Facebook. Desvinculalo y volvé a intentar.",
  "3441042": "Meta todavía no permite conectar la app WhatsApp Business en la región de ese número.",
  "4563015": "La app WhatsApp Business del celular está desactualizada. Actualizala y volvé a intentar.",
};

const texto = (v: unknown): string | undefined =>
  (typeof v === "string" && v.trim()) || (typeof v === "number" && Number.isFinite(v))
    ? String(v).trim()
    : undefined;

/** El motivo de Meta traducido si se conoce el código, o su propio mensaje. */
export const motivoDeMeta = (codigo: unknown, mensaje?: string): string =>
  MOTIVOS_DE_META[texto(codigo) ?? ""] ?? mensaje ?? "Meta no pudo completar la conexión.";

/**
 * Lo que se le dice al admin cuando el backend rechaza la conexión (D29). Nuestros códigos ya vienen con
 * su mensaje en castellano; un `META_ERROR` trae el código de Meta, que se traduce si se conoce.
 */
export function mensajeDelAlta(e: unknown): string {
  const data = (e as { response?: { data?: { code?: unknown; metaCode?: unknown; message?: unknown } } })?.response
    ?.data;
  const mensaje = Array.isArray(data?.message) ? data?.message.join(". ") : texto(data?.message);
  if (data?.code === "META_ERROR") return motivoDeMeta(data.metaCode, mensaje);
  return mensaje ?? texto((e as Error)?.message) ?? "No se pudo conectar el número.";
}

/**
 * `true` si el mensaje viene de Facebook: el hostname es `facebook.com` o un subdominio, por HTTPS. Se
 * compara el hostname y no un prefijo del string, que dejaría pasar `facebook.com.otro.sitio`.
 */
export function esDeFacebook(origin: string): boolean {
  try {
    const url = new URL(origin);
    return (
      url.protocol === "https:" &&
      (url.hostname === "facebook.com" || url.hostname.endsWith(".facebook.com"))
    );
  } catch {
    return false;
  }
}

/** Qué es un `postMessage` del popup, como lo clasifica la tabla de D29. */
export function clasificar(raw: unknown): EventoDeMeta {
  let msg = raw;
  // Meta lo manda como string JSON; se acepta también ya parseado.
  if (typeof msg === "string") {
    try {
      msg = JSON.parse(msg);
    } catch {
      return { kind: "ajeno" };
    }
  }
  if (!msg || typeof msg !== "object" || (msg as { type?: unknown }).type !== TIPO_DE_MENSAJE) {
    return { kind: "ajeno" };
  }
  const { event, data } = msg as { event?: unknown; data?: Record<string, unknown> };
  const evento = texto(event) ?? "";
  const info = data && typeof data === "object" ? data : {};

  if (evento === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING" || evento === "FINISH") {
    return {
      kind: "fin",
      coexistence: evento === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
      wabaId: texto(info.waba_id),
      phoneNumberId: texto(info.phone_number_id),
    };
  }
  if (FINES_NO_SOPORTADOS[evento]) return { kind: "no_soportado", mensaje: FINES_NO_SOPORTADOS[evento] };
  const mensajeDeError = texto(info.error_message);
  if (evento === "ERROR" || evento === "error" || (evento === "CANCEL" && mensajeDeError)) {
    return { kind: "error", mensaje: motivoDeMeta(info.error_code ?? info.error_id, mensajeDeError) };
  }
  if (texto(info.current_step)) return { kind: "paso" };
  if (evento === "CANCEL") return { kind: "cancelado" };
  return { kind: "paso" };
}

/** Lo que se sabe del flujo en curso. */
export interface Flujo {
  code?: string;
  /** El callback de `FB.login` ya volvió, con o sin `code`. */
  loginVolvio: boolean;
  fin?: { coexistence: boolean; wabaId?: string; phoneNumberId?: string };
  /** Se mandó a conectar o se terminó: lo que llegue después no cambia nada. */
  cerrado: boolean;
}

export const FLUJO_NUEVO: Flujo = { loginVolvio: false, cerrado: false };

export type Entrada =
  | { tipo: "mensaje"; data: unknown }
  | { tipo: "login"; code?: string | null }
  | { tipo: "espera_vencida" }
  | { tipo: "timeout" };

export type Paso =
  | { accion: "nada" }
  /** Llegó uno de los dos caminos: esperar `ESPERA_MS` al otro. */
  | { accion: "esperar" }
  | { accion: "conectar"; pedido: ConexionPedida }
  | { accion: "terminar"; resultado: Resultado };

const nada = (flujo: Flujo) => ({ flujo, paso: { accion: "nada" } as Paso });

function conectar(flujo: Flujo, code: string) {
  const pedido: ConexionPedida = { code };
  if (flujo.fin?.wabaId) pedido.wabaId = flujo.fin.wabaId;
  if (flujo.fin?.phoneNumberId) pedido.phoneNumberId = flujo.fin.phoneNumberId;
  if (flujo.fin) pedido.isCoexistence = flujo.fin.coexistence;
  return { flujo: { ...flujo, code, cerrado: true }, paso: { accion: "conectar", pedido } as Paso };
}

const terminar = (flujo: Flujo, resultado: Resultado) => ({
  flujo: { ...flujo, cerrado: true },
  paso: { accion: "terminar", resultado } as Paso,
});

/** Sin `code` no hay nada que canjear: se dice por qué, según lo que haya llegado. */
const sinCode = (flujo: Flujo): Resultado =>
  flujo.fin
    ? { kind: "error", mensaje: "Meta terminó el flujo pero no devolvió la autorización. Volvé a intentar." }
    : { kind: "cancelado" };

/**
 * Un paso del emparejado. El `code` se manda apenas se lo tiene junto con el fin del flujo; si el fin no
 * llega en `ESPERA_MS`, se manda con lo que haya. El primer fin de Coexistence gana: uno normal que llegue
 * después no lo pisa, y sólo suma los ids que traiga.
 */
export function avanzar(flujo: Flujo, entrada: Entrada): { flujo: Flujo; paso: Paso } {
  if (flujo.cerrado) return nada(flujo);

  switch (entrada.tipo) {
    case "mensaje": {
      const evento = clasificar(entrada.data);
      if (evento.kind === "ajeno" || evento.kind === "paso") return nada(flujo);
      if (evento.kind === "fin") {
        const fin = {
          coexistence: Boolean(flujo.fin?.coexistence) || evento.coexistence,
          wabaId: flujo.fin?.wabaId ?? evento.wabaId,
          phoneNumberId: flujo.fin?.phoneNumberId ?? evento.phoneNumberId,
        };
        const conFin = { ...flujo, fin };
        return flujo.code ? conectar(conFin, flujo.code) : nada(conFin);
      }
      // Con un fin ya en la mano, un error o una cancelación posterior es ruido del popup al cerrarse.
      if (flujo.fin) return nada(flujo);
      return terminar(flujo, evento);
    }
    case "login": {
      const code = entrada.code?.trim() || undefined;
      const volvio = { ...flujo, loginVolvio: true, code };
      if (code && flujo.fin) return conectar(volvio, code);
      return { flujo: volvio, paso: { accion: "esperar" } };
    }
    case "espera_vencida":
      return flujo.code ? conectar(flujo, flujo.code) : terminar(flujo, sinCode(flujo));
    case "timeout":
      if (flujo.code) return conectar(flujo, flujo.code);
      return terminar(flujo, {
        kind: "cancelado",
        mensaje: "La conexión con Meta no terminó en 10 minutos. Volvé a intentar.",
      });
  }
}
